// src/Pages/DepreciationMaster.jsx
import { gradients } from '../utils/theme';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, TextField, Button, Divider, Autocomplete, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { Snackbar, Alert } from '@mui/material';
import { getCurrentFYear } from '../utils/financialYear';

export default function DepreciationMaster({ id, mode, onPopupCancel, connectionString, companyId: propsCompanyId }) {
    const { user, selectedCompany, loading } = useAuth();
    const { id: routeId } = useParams();
    const recordId =
        mode === 'edit'
            ? id
            : routeId;

    const navigate = useNavigate();
    // company info
    const userId = user?.UserId || null;
    const companyId =
        (mode === 'comp' || mode === 'edit')
            ? propsCompanyId
            : selectedCompany?.CompId;

    const AppRegID = selectedCompany?.AppRegID || null;

    const fYear = getCurrentFYear();
    const branchId = selectedCompany?.BranchId;

    // ── "Asset's Code" — firm/party this asset's depreciation is tracked against (PartyId) ─
    const [assetValue, setAssetValue] = useState(null);       // { id, FirmName }
    const [assetInput, setAssetInput] = useState('');
    const [assetOptions, setAssetOptions] = useState([]);
    const [assetLoading, setAssetLoading] = useState(false);
    const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);

    // form fields
    const [opBalITLaw, setOpBalITLaw] = useState('');
    const [depreciationITLaw, setDepreciationITLaw] = useState('');
    const [depreciationCompanyLaw, setDepreciationCompanyLaw] = useState('');

    // validation errors
    const [depreciationITLawError, setDepreciationITLawError] = useState('');
    const [depreciationCompanyLawError, setDepreciationCompanyLawError] = useState('');
    const [formError, setFormError] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success', // success | error | warning | info
    });

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

    // ─── Search "Asset's Code" as user types, or list default options on open ──
    useEffect(() => {
        if (!assetDropdownOpen && (!assetInput || assetInput.trim() === '')) {
            setAssetOptions([]);
            return;
        }

        let active = true;
        const timer = setTimeout(async () => {
            setAssetLoading(true);
            try {
                const res = await axios.get(`${connectionString}/api/company-search`, {
                    params: { field: 'FirmName', search: assetInput, companyId },
                });
                if (active) {
                    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                    setAssetOptions(rows);
                }
            } catch (err) {
                console.error('Error fetching asset options:', err);
                if (active) setAssetOptions([]);
            } finally {
                if (active) setAssetLoading(false);
            }
        }, 300);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [assetInput, assetDropdownOpen, companyId, connectionString]);

    // load existing on edit
    useEffect(() => {
        if (mode === 'edit' && recordId) {
            axios.get(`${connectionString}/api/Depreciation/GetDepreciationById/${recordId}`)
                .then(({ data }) => {
                    setOpBalITLaw(data.OpBalITLaw != null ? String(data.OpBalITLaw) : '');
                    setDepreciationITLaw(data.DepreciationITLaw != null ? String(data.DepreciationITLaw) : '');
                    setDepreciationCompanyLaw(data.DepreciationCompanyLaw != null ? String(data.DepreciationCompanyLaw) : '');

                    if (data.PartyId) {
                        const assetLabel = data.FirmName || '';
                        setAssetValue({ id: data.PartyId, FirmName: assetLabel });
                        setAssetInput(assetLabel);
                    }
                })
                .catch(console.error);
        }
    }, [mode, recordId]);

    // validators
    const validatePct = (val, setErr, label) => {
        if (val === '') {
            setErr('');
        } else if (isNaN(val) || val < 0 || val > 100) {
            setErr(`Please enter ${label} between 0% to 100%`);
        } else {
            setErr('');
        }
    };

    const handleDepreciationITLawBlur = () => validatePct(depreciationITLaw, setDepreciationITLawError, 'Depreciation As per I.T. Law');
    const handleDepreciationCompanyLawBlur = () => validatePct(depreciationCompanyLaw, setDepreciationCompanyLawError, 'Depreciation As per Company Law');


    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!assetValue) {
            setFormError("* OOPS! Please select Asset's Code.");
            return;
        }
        if (depreciationITLawError || depreciationCompanyLawError) {
            setFormError('* OOPS! Fill data properly.');
            return;
        }
        setFormError('');
        try {
            const isEdit = !(mode === 'add' || mode === 'comp');
            const payload = {
                PartyId: assetValue?.id || null,
                OpBalITLaw: parseFloat(opBalITLaw) || 0,
                DepreciationITLaw: parseFloat(depreciationITLaw) || 0,
                DepreciationCompanyLaw: parseFloat(depreciationCompanyLaw) || 0,
                companyId,
                AppRegID,
                fYear,
                branchId,
                userId,
                isEdit,
            };
            console.log('Payload', payload);
            await axios.post(`${connectionString}/api/Depreciation/Save`, payload);
            showSnackbar(`Data ${isEdit ? 'updated' : 'inserted'} successfully`, 'success');

            setTimeout(() => {
                if (onPopupCancel) {
                    onPopupCancel();
                } else {
                    navigate('/main/depreciationDetail');
                }
            }, 600);
        } catch (err) {
            console.error(err);
            showSnackbar(
                `Error ${mode === 'add' || mode === 'comp' ? 'inserting' : 'updating'} data`,
                'error'
            );
        }
    };

    const handleCancel = () => {
        if (onPopupCancel) onPopupCancel();
        else navigate('/main/depreciationDetail');
    };

    // ──────────────── Alt+S (Save/Update) & Alt+C (Cancel) shortcuts ────────────────
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                handleSubmit(e);
            } else if (e.code === "KeyC") {
                e.preventDefault();
                handleCancel();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    return (
        <>
            <Box sx={{ height: 4, background: gradients.primaryRow }} />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                    {mode === "add" || mode === "comp" ? "Depreciation Details Confirmation" : "Edit Depreciation Details"}
                </Typography>
            </Box>
            <Divider />
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                        <Autocomplete
                            fullWidth
                            options={assetOptions}
                            loading={assetLoading}
                            open={assetDropdownOpen}
                            onOpen={() => setAssetDropdownOpen(true)}
                            onClose={() => setAssetDropdownOpen(false)}
                            getOptionLabel={(opt) => opt?.FirmName || ''}
                            isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                            value={assetValue}
                            inputValue={assetInput}
                            onInputChange={(_, newInput) => setAssetInput(newInput)}
                            onChange={(_, newValue) => setAssetValue(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label="Asset's Code"
                                    size="small"
                                    InputLabelProps={{ sx: requiredAsteriskStyle }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {assetLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{ '& .MuiInputBase-root': { minHeight: 38 } }}
                                />
                            )}
                        />

                        {/* Single column: Op.Bal. (I.T. Law), Depreciation (I.T. Law %), Depreciation (Company Law %) */}
                        <TextField fullWidth label="Op.Bal. As per I.T. Law" type="number" size="small" value={opBalITLaw} onChange={e => setOpBalITLaw(e.target.value)} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                        <TextField fullWidth label="Depreciation As per Company Law (%)" type="number" size="small" value={depreciationCompanyLaw} onChange={e => setDepreciationCompanyLaw(e.target.value)} onBlur={handleDepreciationCompanyLawBlur} error={!!depreciationCompanyLawError} helperText={depreciationCompanyLawError} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                        <TextField fullWidth label="Depreciation As per I.T. Law (%)" type="number" size="small" value={depreciationITLaw} onChange={e => setDepreciationITLaw(e.target.value)} onBlur={handleDepreciationITLawBlur} error={!!depreciationITLawError} helperText={depreciationITLawError} sx={{ '& .MuiInputBase-root': { height: 38 } }} />
                    </Box>
                    {formError && (
                        <Typography color="error" align="center" sx={{ mt: 1 }}>
                            {formError}
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleCancel} type="button" variant="outlined" color="inherit" size="small">
                        Cancel
                    </Button>
                    <Button variant="contained" type="submit" size="small">
                        {mode === 'add' || mode === "comp" ? 'Save' : 'Update'}
                    </Button>
                </Box>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
